/**
 * AdminPrimKurallari.jsx
 *
 * Danışman CRM Faz 1/2 — Prim Kuralları (admin-only).
 * Route: /admin/prim-kurallari
 *
 * Backend: routes/prim.py
 *   GET/POST /admin/prim-kurallari
 *   PUT/DELETE /admin/prim-kurallari/{id}  (409 prim_kurali_cakisiyor,
 *     DELETE soft-delete — mali/denetim kaydı korunur)
 *
 * Faz 2: sabit oran (oran_yuzde) YANINDA kademeli (hesaplama_tipi +
 * kademeler) modeli desteklenir — TAM OLARAK BİRİ dolu olabilir (backend
 * validasyonu). GET listesinde her kural için `kademeler_normalized`
 * (legacy dahil tek tip görünüm) döner.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import HakedisPenceresiRozeti from '../components/admin/HakedisPenceresiRozeti';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import { useDanismanlarOptions, danismanOptionsList } from '../hooks/useDanismanlarOptions';
import { fmtMoney, fmtPercent } from '../utils/money';
import { trNowLocalDateInput } from '../utils/trDateTime';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Percent, Plus, AlertTriangle, RefreshCw, Info, Pencil, Trash2, X, Layers,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LBL_CLS = 'text-[11.5px] font-bold uppercase tracking-wide text-[var(--crm-muted-2)] mb-1 block';
const INPUT_CLS = 'w-full px-3 py-2 rounded-lg border text-sm font-semibold bg-white focus:outline-none focus:ring-2';
const INPUT_STYLE = { borderColor: 'var(--crm-input-border)', color: 'var(--crm-navy-text)' };

const emptyForm = () => ({
  scope: 'genel',
  danisman_id: '',
  kuralTipi: 'sabit', // 'sabit' | 'kademeli'
  oran_yuzde: '',
  hesaplamaTipi: 'marjinal', // 'marjinal' | 'toplam'
  kademeler: [{ esik_tutar: '0', oran: '' }],
  gecerli_baslangic: trNowLocalDateInput(),
  gecerli_bitis: '',
  aciklama: '',
});

function formFromKural(k) {
  const isKademeli = Array.isArray(k.kademeler) && k.kademeler.length > 0;
  return {
    scope: k.scope,
    danisman_id: k.danisman_id || '',
    kuralTipi: isKademeli ? 'kademeli' : 'sabit',
    oran_yuzde: isKademeli ? '' : String(k.oran_yuzde ?? ''),
    hesaplamaTipi: k.hesaplama_tipi || 'marjinal',
    kademeler: isKademeli
      ? k.kademeler.map((kd) => ({ esik_tutar: String(kd.esik_tutar), oran: String(kd.oran) }))
      : [{ esik_tutar: '0', oran: '' }],
    gecerli_baslangic: k.gecerli_baslangic || trNowLocalDateInput(),
    gecerli_bitis: k.gecerli_bitis || '',
    aciklama: k.aciklama || '',
  };
}

// ─── Kademeli hesaplama — backend routes/prim.py::_kademeli_hesapla ile
// AYNI mantık (client-side "≈" önizleme; backend'in kuruş hassasiyetiyle
// birebir eşleşmesi ZORUNLU değil). ────────────────────────────────────────
function hesaplaKademeli(tutar, kademelerRaw, hesaplamaTipi) {
  const kademeler = kademelerRaw
    .map((k) => ({ esik_tutar: Number(k.esik_tutar || 0), oran: Number(k.oran || 0) }))
    .sort((a, b) => a.esik_tutar - b.esik_tutar);
  if (kademeler.length === 0) return 0;

  if (hesaplamaTipi === 'toplam') {
    let uygulanan = kademeler[0];
    for (const k of kademeler) {
      if (tutar >= k.esik_tutar) uygulanan = k;
    }
    return Math.round(tutar * uygulanan.oran) / 100;
  }

  let toplam = 0;
  for (let i = 0; i < kademeler.length; i++) {
    const alt = kademeler[i].esik_tutar;
    const ust = i + 1 < kademeler.length ? kademeler[i + 1].esik_tutar : null;
    const dilim = Math.max(0, (ust !== null ? Math.min(tutar, ust) : tutar) - alt);
    if (dilim <= 0) continue;
    toplam += (dilim * kademeler[i].oran) / 100;
  }
  return Math.round(toplam * 100) / 100;
}

function validateKademeler(kademeler) {
  for (let i = 1; i < kademeler.length; i++) {
    const prev = Number(kademeler[i - 1].esik_tutar || 0);
    const cur = kademeler[i].esik_tutar === '' ? NaN : Number(kademeler[i].esik_tutar);
    if (Number.isNaN(cur) || cur <= prev) return 'esik_sirasi';
  }
  for (const k of kademeler) {
    if (k.oran === '' || Number.isNaN(Number(k.oran)) || Number(k.oran) < 0 || Number(k.oran) > 100) return 'oran_eksik';
  }
  return null;
}

// ─── Kural tipi bilgi popover'ı ─────────────────────────────────────────
function HesaplamaTipiInfo({ tr }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={tr('Marjinal ile Toplam Tutara arasındaki fark', 'Difference between Marginal and Whole Amount')}
          className="p-0.5 rounded-full"
          style={{ color: 'var(--crm-info)' }}
        >
          <Info size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-xs space-y-2" style={{ color: 'var(--crm-navy-text)' }}>
        <p className="font-bold text-sm">{tr('Marjinal ile Toplam Tutara arasındaki fark', 'Difference between Marginal and Whole Amount')}</p>
        <div>
          <p className="font-bold" style={{ color: 'var(--crm-info)' }}>{tr('Marjinal', 'Marginal')}</p>
          <p>{tr('Her kademe sadece kendi aralığındaki tutara uygulanır — gelir vergisi dilimleri gibi.', 'Each tier only applies to the amount within its own range — like income tax brackets.')}</p>
          <p className="italic mt-0.5" style={{ color: 'var(--crm-muted)' }}>
            {tr('Örnek: 0–50.000₺ %5, üzeri %7 → 80.000₺: 2.500+2.100=4.600₺', 'Example: 0–50,000₺ 5%, above 7% → 80,000₺: 2,500+2,100=4,600₺')}
          </p>
        </div>
        <div>
          <p className="font-bold" style={{ color: 'var(--crm-info)' }}>{tr('Toplam Tutara', 'Whole Amount')}</p>
          <p>{tr('Ulaştığınız en yüksek kademenin oranı tüm tutara uygulanır.', 'The rate of the highest tier reached applies to the entire amount.')}</p>
          <p className="italic mt-0.5" style={{ color: 'var(--crm-muted)' }}>
            {tr('Örnek: 80.000₺ × %7 = 5.600₺', 'Example: 80,000₺ × 7% = 5,600₺')}
          </p>
        </div>
        <p className="p-2 rounded-lg font-semibold" style={{ backgroundColor: 'rgba(201,162,39,0.12)', color: 'var(--crm-gold-dark)' }}>
          {tr('İki yöntem aynı kademelerde farklı tutar üretir — seçiminizi danışmanlara açıkça bildirin.', 'The two methods produce different amounts for the same tiers — communicate your choice clearly to consultants.')}
        </p>
      </PopoverContent>
    </Popover>
  );
}

// ─── Kural formu (yeni / düzenle) ────────────────────────────────────────
function KuralFormDialog({ open, onClose, onSaved, danismanlar, editingKural, tr, lang, authHeaders }) {
  const toast = useToast();
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [ornekCiro, setOrnekCiro] = useState('80000');

  useEffect(() => {
    if (open) {
      setForm(editingKural ? formFromKural(editingKural) : emptyForm());
      setErrMsg('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingKural]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addKademe = () => setForm((p) => ({ ...p, kademeler: [...p.kademeler, { esik_tutar: '', oran: '' }] }));
  const removeKademe = (idx) => setForm((p) => ({ ...p, kademeler: p.kademeler.filter((_, i) => i !== idx) }));
  const setKademe = (idx, field, val) => setForm((p) => ({
    ...p, kademeler: p.kademeler.map((k, i) => (i === idx ? { ...k, [field]: val } : k)),
  }));

  const kademeHata = form.kuralTipi === 'kademeli' ? validateKademeler(form.kademeler) : null;

  const valid = form.gecerli_baslangic && (form.scope === 'genel' || form.danisman_id) && (
    form.kuralTipi === 'sabit'
      ? (form.oran_yuzde !== '' && Number(form.oran_yuzde) >= 0 && Number(form.oran_yuzde) <= 100)
      : (form.kademeler.length >= 1 && !kademeHata)
  );

  const isEdit = !!editingKural;

  const submit = async () => {
    if (!valid) return;
    setSaving(true); setErrMsg('');
    try {
      const body = {
        gecerli_baslangic: form.gecerli_baslangic,
        gecerli_bitis: form.gecerli_bitis || null,
        aciklama: form.aciklama.trim(),
      };
      if (!isEdit) {
        body.scope = form.scope;
        body.danisman_id = form.scope === 'danisman' ? form.danisman_id : null;
      }
      if (form.kuralTipi === 'sabit') {
        body.oran_yuzde = Number(form.oran_yuzde);
      } else {
        body.hesaplama_tipi = form.hesaplamaTipi;
        body.kademeler = form.kademeler.map((k) => ({ esik_tutar: Number(k.esik_tutar), oran: Number(k.oran) }));
      }
      if (isEdit) {
        await axios.put(`${API}/admin/prim-kurallari/${editingKural.kural_id}`, body, { headers: authHeaders() });
        toast.success(tr('Prim kuralı güncellendi', 'Commission rule updated'));
      } else {
        await axios.post(`${API}/admin/prim-kurallari`, body, { headers: authHeaders() });
        toast.success(tr('Prim kuralı oluşturuldu', 'Commission rule created'));
      }
      onSaved();
      onClose();
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === 'prim_kurali_cakisiyor') {
        setErrMsg(tr('Bu aralıkta çakışan aktif kural var', 'An overlapping active rule already exists for this range'));
      } else {
        setErrMsg(getApiErrorMessage(err, lang));
      }
    } finally {
      setSaving(false);
    }
  };

  const ornekSonuc = form.kuralTipi === 'kademeli' && !kademeHata
    ? hesaplaKademeli(Number(ornekCiro || 0), form.kademeler, form.hesaplamaTipi)
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold" style={{ color: 'var(--crm-navy-text)' }}>
            {isEdit ? tr('Prim Kuralını Düzenle', 'Edit Commission Rule') : tr('Yeni Prim Kuralı', 'New Commission Rule')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className={LBL_CLS}>{tr('Kapsam', 'Scope')}</label>
            <div className="flex gap-2">
              {[['genel', tr('Genel', 'General')], ['danisman', tr('Danışmana Özel', 'Consultant-Specific')]].map(([v, lbl]) => (
                <button
                  key={v}
                  type="button"
                  disabled={isEdit}
                  onClick={() => set('scope', v)}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-bold border-2 disabled:opacity-50"
                  style={form.scope === v
                    ? { backgroundColor: 'var(--crm-navy)', color: 'white', borderColor: 'var(--crm-navy)' }
                    : { backgroundColor: 'white', color: 'var(--crm-navy-text)', borderColor: 'var(--crm-card-border)' }}
                >
                  {lbl}
                </button>
              ))}
            </div>
            {isEdit && (
              <p className="text-[10.5px] font-semibold mt-1" style={{ color: 'var(--crm-muted-2)' }}>
                {tr('Kapsam düzenlemede değiştirilemez.', 'Scope cannot be changed while editing.')}
              </p>
            )}
          </div>
          {form.scope === 'danisman' && (
            <div>
              <label className={LBL_CLS}>{tr('Danışman', 'Consultant')} <span style={{ color: 'var(--crm-danger)' }}>*</span></label>
              <select disabled={isEdit} value={form.danisman_id} onChange={(e) => set('danisman_id', e.target.value)} className={`${INPUT_CLS} disabled:opacity-50`} style={INPUT_STYLE}>
                <option value="">{tr('Seçiniz', 'Select')}</option>
                {/* Pasif danışmanlar da seçilebilir kalır — mevcut kurallar
                    bozulmasın (bkz. hooks/useDanismanlarOptions.js). */}
                {danismanOptionsList(danismanlar, tr).map((o) => (
                  <option key={o.key} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Kural tipi toggle: Sabit Oran / Kademeli */}
          <div>
            <label className={LBL_CLS}>{tr('Kural Tipi', 'Rule Type')}</label>
            <div className="flex gap-2" role="radiogroup" aria-label={tr('Kural Tipi', 'Rule Type')}>
              {[['sabit', tr('Sabit Oran', 'Flat Rate')], ['kademeli', tr('Kademeli', 'Tiered')]].map(([v, lbl]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={form.kuralTipi === v}
                  aria-pressed={form.kuralTipi === v}
                  onClick={() => set('kuralTipi', v)}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-bold border-2 flex items-center justify-center gap-1"
                  style={form.kuralTipi === v
                    ? { backgroundColor: 'var(--crm-navy)', color: 'white', borderColor: 'var(--crm-navy)' }
                    : { backgroundColor: 'white', color: 'var(--crm-navy-text)', borderColor: 'var(--crm-card-border)' }}
                >
                  {v === 'kademeli' && <Layers size={12} />} {lbl}
                </button>
              ))}
            </div>
          </div>

          {form.kuralTipi === 'sabit' ? (
            <div>
              <label className={LBL_CLS}>{tr('Oran %', 'Rate %')} <span style={{ color: 'var(--crm-danger)' }}>*</span></label>
              <input type="number" min="0" max="100" step="0.1" value={form.oran_yuzde} onChange={(e) => set('oran_yuzde', e.target.value)} className={INPUT_CLS} style={INPUT_STYLE} />
            </div>
          ) : (
            <div className="space-y-2.5 p-3 rounded-lg" style={{ backgroundColor: 'var(--crm-cream)', border: '1px solid var(--crm-card-border)' }}>
              {/* Hesaplama tipi seçici */}
              <div>
                <label className={LBL_CLS}>{tr('Hesaplama Yöntemi', 'Calculation Method')}</label>
                <div className="flex items-center gap-2">
                  <div className="flex gap-2 flex-1">
                    {[['marjinal', tr('Marjinal', 'Marginal')], ['toplam', tr('Toplam Tutara', 'Whole Amount')]].map(([v, lbl]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => set('hesaplamaTipi', v)}
                        className="flex-1 px-3 py-1.5 rounded-lg text-xs font-bold border-2"
                        style={form.hesaplamaTipi === v
                          ? { backgroundColor: 'var(--crm-navy)', color: 'white', borderColor: 'var(--crm-navy)' }
                          : { backgroundColor: 'white', color: 'var(--crm-navy-text)', borderColor: 'var(--crm-card-border)' }}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                  <HesaplamaTipiInfo tr={tr} />
                </div>
                <p className="text-[10.5px] font-semibold mt-1 p-1.5 rounded" style={{ backgroundColor: 'rgba(201,162,39,0.10)', color: 'var(--crm-gold-dark)' }}>
                  {tr('İki yöntem aynı kademelerde farklı tutar üretir — seçiminizi danışmanlara açıkça bildirin.', 'The two methods produce different amounts for the same tiers — communicate your choice clearly to consultants.')}
                </p>
              </div>

              {/* Kademe listesi */}
              <div>
                <label className={LBL_CLS}>{tr('Kademeler', 'Tiers')}</label>
                <div className="space-y-1.5">
                  {form.kademeler.map((k, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      {idx === 0 ? (
                        <span className="flex-1 min-w-0 px-3 py-2 rounded-lg text-xs font-bold bg-white border" style={{ borderColor: 'var(--crm-input-border)', color: 'var(--crm-muted)' }}>
                          0 ₺ ({tr('taban', 'base')})
                        </span>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          value={k.esik_tutar}
                          onChange={(e) => setKademe(idx, 'esik_tutar', e.target.value)}
                          placeholder={tr('Eşik (₺)', 'Threshold (₺)')}
                          className={`${INPUT_CLS} flex-1 min-w-0`}
                          style={INPUT_STYLE}
                        />
                      )}
                      {/* !w-24: INPUT_CLS'nin w-full'ı Tailwind'in derlenmiş
                          CSS'inde w-24'ten SONRA gelir (aynı özellik, eşit
                          özgüllük → kaskadda sonraki kazanır), bu yüzden
                          w-full sessizce w-24'ü eziyordu ve bu alan eşik
                          alanının genişliğini çalıp 100%'e yayılıyordu.
                          !important ile sabit genişliği garanti altına al. */}
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={k.oran}
                        onChange={(e) => setKademe(idx, 'oran', e.target.value)}
                        placeholder={tr('Oran %', 'Rate %')}
                        className={`${INPUT_CLS} !w-24 flex-shrink-0`}
                        style={INPUT_STYLE}
                      />
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => removeKademe(idx)}
                          aria-label={tr('Kademeyi kaldır', 'Remove tier')}
                          className="p-2 rounded-lg flex-shrink-0"
                          style={{ color: 'var(--crm-danger)' }}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addKademe}
                  className="mt-2 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 border-2"
                  style={{ borderColor: 'var(--crm-navy)', color: 'var(--crm-navy)', backgroundColor: 'white' }}
                >
                  <Plus size={12} /> {tr('Kademe Ekle', 'Add Tier')}
                </button>
                {kademeHata === 'esik_sirasi' && (
                  <p className="text-[11px] font-bold mt-1.5 flex items-center gap-1" style={{ color: 'var(--crm-danger)' }}>
                    <AlertTriangle size={11} /> {tr('Her eşik bir öncekinden büyük olmalı', 'Each threshold must be greater than the previous one')}
                  </p>
                )}
                {kademeHata === 'oran_eksik' && (
                  <p className="text-[11px] font-bold mt-1.5 flex items-center gap-1" style={{ color: 'var(--crm-danger)' }}>
                    <AlertTriangle size={11} /> {tr('Tüm kademelerde oran girilmeli', 'Rate is required for every tier')}
                  </p>
                )}
              </div>

              {/* Canlı örnek paneli */}
              <div className="pt-1">
                <label className={LBL_CLS}>{tr('Canlı Örnek', 'Live Example')}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={ornekCiro}
                    onChange={(e) => setOrnekCiro(e.target.value)}
                    className={`${INPUT_CLS} flex-1`}
                    style={INPUT_STYLE}
                    placeholder={tr('Örnek ciro (₺)', 'Example revenue (₺)')}
                  />
                  <span className="text-sm font-bold whitespace-nowrap" style={{ color: 'var(--crm-gold-dark)' }}>
                    {ornekSonuc != null ? `≈ ${fmtMoney(ornekSonuc)} ${tr('hakediş', 'earnings')}` : '—'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LBL_CLS}>{tr('Geçerli Başlangıç', 'Valid From')} <span style={{ color: 'var(--crm-danger)' }}>*</span></label>
              <input type="date" value={form.gecerli_baslangic} onChange={(e) => set('gecerli_baslangic', e.target.value)} className={INPUT_CLS} style={INPUT_STYLE} />
            </div>
            <div>
              <label className={LBL_CLS}>{tr('Geçerli Bitiş', 'Valid Until')} <span className="font-normal normal-case" style={{ color: 'var(--crm-muted-2)' }}>({tr('opsiyonel', 'optional')})</span></label>
              <input type="date" value={form.gecerli_bitis} onChange={(e) => set('gecerli_bitis', e.target.value)} className={INPUT_CLS} style={INPUT_STYLE} />
            </div>
          </div>
          <div>
            <label className={LBL_CLS}>{tr('Açıklama', 'Description')}</label>
            <textarea value={form.aciklama} onChange={(e) => set('aciklama', e.target.value)} rows={2} className={`${INPUT_CLS} resize-none`} style={INPUT_STYLE} />
          </div>
          {errMsg && (
            <p className="text-xs font-bold flex items-center gap-1" style={{ color: 'var(--crm-danger)' }}>
              <AlertTriangle size={12} /> {errMsg}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-100" style={{ color: 'var(--crm-navy-text)' }}>
            {tr('Vazgeç', 'Cancel')}
          </button>
          <button
            onClick={submit}
            disabled={saving || !valid}
            className="px-5 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--crm-success-btn)' }}
          >
            {saving ? '...' : tr('Kaydet', 'Save')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Silme onay dialogu ──────────────────────────────────────────────────
function DeleteConfirmDialog({ kural, onClose, onDeleted, tr, lang, authHeaders }) {
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/prim-kurallari/${kural.kural_id}`, { headers: authHeaders() });
      toast.success(tr('Prim kuralı silindi', 'Commission rule deleted'));
      onDeleted();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!kural} onOpenChange={(o) => { if (!o && !deleting) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ color: 'var(--crm-navy-text)' }}>
            <Trash2 size={18} style={{ color: 'var(--crm-danger)' }} />
            {tr('Prim Kuralını Sil', 'Delete Commission Rule')}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm" style={{ color: 'var(--crm-navy-text)' }}>
          {tr('Bu kural artık kullanılamayacak. Geçmiş hakediş kayıtları etkilenmez (mali kayıt korunur).', 'This rule will no longer be usable. Past earnings records are not affected (financial history is preserved).')}
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={deleting} className="px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-100" style={{ color: 'var(--crm-navy-text)' }}>
            {tr('Vazgeç', 'Cancel')}
          </button>
          <button
            onClick={doDelete}
            disabled={deleting}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 flex items-center gap-1.5"
            style={{ backgroundColor: 'var(--crm-danger)' }}
          >
            <Trash2 size={14} /> {deleting ? '...' : tr('Sil', 'Delete')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPrimKurallari() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [kurallar, setKurallar] = useState([]);
  // Prim kuralı danışman seçimi — HER ZAMAN aktif=tumu, pasifler de seçilebilir
  // kalır (mevcut kurallar bozulmasın), bkz. hooks/useDanismanlarOptions.js.
  const { danismanlar } = useDanismanlarOptions(authHeaders, { includeAdmins: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingKural, setEditingKural] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchKurallar = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(`${API}/admin/prim-kurallari`, { headers: authHeaders() });
      setKurallar(r.data.kurallar || []);
    } catch (err) { setError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => { fetchKurallar(); }, [fetchKurallar]);

  const danismanAdi = (id) => danismanlar.find((d) => d.user_id === id)?.name || id;

  const togglePasif = async (kural) => {
    setTogglingId(kural.kural_id);
    try {
      await axios.put(`${API}/admin/prim-kurallari/${kural.kural_id}`, { aktif: !kural.aktif }, { headers: authHeaders() });
      toast.success(kural.aktif ? tr('Kural pasifleştirildi', 'Rule deactivated') : tr('Kural etkinleştirildi', 'Rule activated'));
      fetchKurallar();
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === 'prim_kurali_cakisiyor') {
        toast.error(tr('Bu aralıkta çakışan aktif kural var', 'An overlapping active rule already exists for this range'));
      } else {
        toast.error(getApiErrorMessage(err, lang));
      }
    } finally {
      setTogglingId(null);
    }
  };

  const oranHucresi = (k) => {
    const norm = k.kademeler_normalized || [];
    if (norm.length > 1 || Array.isArray(k.kademeler) && k.kademeler.length > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ backgroundColor: 'rgba(201,162,39,0.12)', color: 'var(--crm-gold-dark)' }}>
          <Layers size={10} /> {tr('Kademeli', 'Tiered')} ({norm.length})
        </span>
      );
    }
    return <span style={{ color: 'var(--crm-gold-dark)' }}>{fmtPercent(k.oran_yuzde, lang)}</span>;
  };

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: tr('Prim Kuralları', 'Commission Rules') }]}
          title={tr('Prim Kuralları', 'Commission Rules')}
          subtitle={tr('Danışman hakediş hesaplarında kullanılan prim oranlarını yönetin', 'Manage commission rates used in consultant earnings calculations')}
          actions={
            <button
              onClick={() => { setEditingKural(null); setFormOpen(true); }}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white flex items-center gap-1.5"
              style={{ backgroundColor: 'var(--crm-navy)' }}
            >
              <Plus size={15} /> {tr('Yeni Kural', 'New Rule')}
            </button>
          }
        />

        <div className="mb-4">
          <HakedisPenceresiRozeti tr={tr} lang={lang} />
        </div>

        {error && !loading && (
          <div className="mb-4 p-3 rounded-xl flex items-center justify-between gap-3" style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}>
            <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--crm-danger)' }}>
              <AlertTriangle size={16} /> {tr('Bağlantı koptu — tekrar dene', 'Connection lost — try again')}
            </span>
            <button onClick={fetchKurallar} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1" style={{ backgroundColor: 'var(--crm-danger)' }}>
              <RefreshCw size={13} /> {tr('Tekrar Dene', 'Retry')}
            </button>
          </div>
        )}

        {loading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        )}

        {!loading && !error && kurallar.length === 0 && (
          <div className="bg-white p-8 text-center" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <Percent size={32} className="mx-auto mb-2" style={{ color: 'var(--crm-muted-2)' }} />
            <p className="text-sm font-semibold mb-3" style={{ color: 'var(--crm-muted)' }}>
              {tr('Henüz prim kuralı tanımlanmamış.', 'No commission rules defined yet.')}
            </p>
            <button
              onClick={() => { setEditingKural(null); setFormOpen(true); }}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white inline-flex items-center gap-1.5"
              style={{ backgroundColor: 'var(--crm-navy)' }}
            >
              <Plus size={15} /> {tr('Yeni Kural', 'New Rule')}
            </button>
          </div>
        )}

        {!loading && !error && kurallar.length > 0 && (
          <div className="bg-white overflow-hidden" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: 'var(--crm-cream)' }}>
                  <tr>
                    <th className="text-left p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Ad', 'Name')}</th>
                    <th className="text-left p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Kapsam / Danışman', 'Scope / Consultant')}</th>
                    <th className="text-right p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Oran', 'Rate')}</th>
                    <th className="text-left p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Geçerlilik', 'Validity')}</th>
                    <th className="text-center p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Durum', 'Status')}</th>
                    <th className="text-center p-3 font-bold" style={{ color: 'var(--crm-muted-2)' }}>{tr('İşlem', 'Action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {kurallar.map((k) => (
                    <tr key={k.kural_id} className="border-t" style={{ borderColor: 'var(--crm-card-border)' }}>
                      <td className="p-3 font-semibold" style={{ color: 'var(--crm-navy-text)' }}>{k.aciklama || (k.scope === 'genel' ? tr('Genel Kural', 'General Rule') : tr('Danışmana Özel', 'Consultant Rule'))}</td>
                      <td className="p-3 text-xs" style={{ color: 'var(--crm-muted)' }}>
                        {k.scope === 'genel' ? tr('Genel', 'General') : danismanAdi(k.danisman_id)}
                      </td>
                      <td className="p-3 text-right font-bold">{oranHucresi(k)}</td>
                      <td className="p-3 text-xs" style={{ color: 'var(--crm-muted)' }}>
                        {k.gecerli_baslangic} — {k.gecerli_bitis || tr('süresiz', 'indefinite')}
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className="px-2 py-0.5 rounded-full text-[11px] font-bold"
                          style={k.aktif
                            ? { backgroundColor: 'rgba(29,138,76,0.10)', color: 'var(--crm-success)' }
                            : { backgroundColor: 'var(--crm-cream)', color: 'var(--crm-muted)' }}
                        >
                          {k.aktif ? tr('Aktif', 'Active') : tr('Pasif', 'Inactive')}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <Switch
                            checked={k.aktif}
                            disabled={togglingId === k.kural_id}
                            onCheckedChange={() => togglePasif(k)}
                            aria-label={k.aktif ? tr('Pasifleştir', 'Deactivate') : tr('Etkinleştir', 'Activate')}
                          />
                          <button
                            onClick={() => { setEditingKural(k); setFormOpen(true); }}
                            aria-label={tr('Kuralı Düzenle', 'Edit Rule')}
                            className="p-1.5 rounded-lg"
                            style={{ color: 'var(--crm-navy)' }}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(k)}
                            aria-label={tr('Kuralı Sil', 'Delete Rule')}
                            className="p-1.5 rounded-lg"
                            style={{ color: 'var(--crm-danger)' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <KuralFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingKural(null); }}
        onSaved={fetchKurallar}
        danismanlar={danismanlar}
        editingKural={editingKural}
        tr={tr}
        lang={lang}
        authHeaders={authHeaders}
      />
      <DeleteConfirmDialog
        kural={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); fetchKurallar(); }}
        tr={tr}
        lang={lang}
        authHeaders={authHeaders}
      />
    </AdminLayout>
  );
}
